<script setup lang="ts">
import type { Locale } from 'lc-services/types'
import type { HouseDetails } from '~/types/house/types'

const { house, guideUrl } = defineProps<{
  house: HouseDetails
  guideUrl: string
}>()

const { t } = useI18n<unknown, Locale>({ useScope: 'local' })
const url = useRequestURL()
const image = useImage()

const { isSmallScreen } = useBreakpoint()
const { trackEvent } = useTracking()

const imageExperience = computed(() =>
  isSmallScreen.value
    ? 'production/assets/images/experiences/experiences-mobile.png'
    : 'production/assets/images/experiences/experiences-desktop.png',
)

const guidUrlHubspot = computed(() => {
  const imageUrl = image(house.firstPhoto.url, { width: 400, quality: 65 })
  const urlHubspot = new URL(guideUrl)
  urlHubspot.searchParams.set('villa_name', house.name)
  urlHubspot.searchParams.set('refer_page', url.href)
  urlHubspot.searchParams.set('villa_img', imageUrl)
  return urlHubspot.toString()
})

const handleTracking = () => {
  trackEvent({
    event: 'product_page_experiences_click',
    product: {
      ...buildTrackEventProduct({
        houseId: house.id,
        houseName: house.name,
        iconic: house.iconicCollection,
        onlineBookingAvailable: house.onlineReservation,
      }),
    },
  })
}
</script>

<template>
  <div
    class="-mx-4 mb-7 flex flex-col items-center bg-beige lg:mx-0 lg:flex-row lg:justify-between lg:pl-8"
  >
    <div
      class="mb-4 mt-6 text-center lg:-mt-10 lg:mb-0 lg:min-w-[300px] lg:max-w-[400px] lg:text-left"
    >
      <h2 class="mb-1 text-3xl lg:mb-3 lg:text-2xl">{{ t('title') }}</h2>
      <p class="mb-4 max-w-[350px] text-md lg:max-w-full lg:text-base">
        {{ t('text') }}
      </p>
      <BaseButton
        :href="guidUrlHubspot"
        class="!text-lg"
        color="secondary"
        font-weight="font-semibold"
        target="_blank"
        @click="handleTracking"
      >
        {{ t('cta') }}
      </BaseButton>
    </div>
    <div class="w-full max-w-[600px] lg:w-[342px]">
      <BaseNuxtImg
        :height="isSmallScreen ? 424 : 350"
        :src="imageExperience"
        :width="isSmallScreen ? 600 : 342"
        alt="experiences"
        loading="lazy"
      />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "title": "Choose the extraordinary",
    "text": "Get inspired by our myriad of unforgettable experiences.",
    "cta": "Explore"
  },
  "fr": {
    "title": "Choisissez l'extraordinaire",
    "text": "Laissez-vous inspirer par notre myriade d'expériences inoubliables.",
    "cta": "Découvrir"
  }
}
</i18n>
